import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { KeyboardManager } from '@inter-ikea-kompis/keyboard-manager';
import type { IKompisTranslations, ILocalizedInformation, ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Text } from '@ingka/common-styles-webc';
import { HelperText as SkapaHelperText } from '@ingka/helper-text-webc/passive.js';
import { registerIcon, warningTriangle } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import type { InlineMessageVariant } from '@ingka/inline-message-webc';
import { InlineMessage as SkapaInlineMessage } from '@ingka/inline-message-webc/passive.js';
import type { InputFieldValidState } from '@ingka/input-field-webc';
import { InputField as SkapaInputField } from '@ingka/input-field-webc/passive.js';

import ZipInCardStateEnum from '../../enums/ZipInCardStateEnum.js';
import type IZipInTranslations from '../../types/IZipInTranslations.js';
import ZipInCardStyle from './ZipInCardStyle.js';

/**
 * Generic component for handling zip code input. Is intended for use inside the ZipIn component.
 *
 * @event save Triggered when the save button is clicked.
 * @event input Triggered when the zip code is changed.
 */

export default class ZipInCard extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IZipInTranslations | null = null;

	// Current zip code. Changing this will update the value of the input field.
	@property({ type: String })
	public zipCode: string | null = null;

	// Card state. Use ZipInCardStateEnum.
	@property({ type: String })
	public state = ZipInCardStateEnum.default;

	private keyboardManager = new KeyboardManager();

	public override async connectedCallback(): Promise<void> {
		registerIcon(warningTriangle);

		this.customElements.define('skapa-input-field', SkapaInputField);
		this.customElements.define('skapa-inline-message', SkapaInlineMessage);
		this.customElements.define('skapa-icon', SkapaIcon);
		this.customElements.define('skapa-helper-text', SkapaHelperText);

		super.connectedCallback();
	}

	public override render(): RenderResult {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslations()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		return html`
			<style>
				${ZipInCardStyle.getStyle({
					theme: this.theme
				})}
			</style>
			${this.getHeading()} ${this.getBody()} ${this.getInput()} ${this.getInlineMessage()}
		`;
	}

	/**
	 * Internal method to retrieve zip code directly from input field. When using
	 * this component internally, prefer this over listening on the `input` event.
	 */
	public getZipCode(): string {
		return this.getInputElement()?.value ?? '';
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (changedProperties.has('zipCode')) {
			const input = this.getInputElement();
			if (input) {
				input.value = this.zipCode ?? '';
			}
		}

		return true;
	}

	private getInputElement(): HTMLInputElement | null {
		return this.shadowRoot?.querySelector('input');
	}

	private hasTranslations(): boolean {
		return !!this.translations || !!this.localizedInformation?.translations;
	}

	private getTranslations(): IZipInTranslations {
		const translations = this.translations
			? this.translations
			: <IKompisTranslations>this.localizedInformation?.translations;

		return translations;
	}

	private getHeading(): RenderResult {
		return html`
			<h2 class="heading ${Text.classnames({ heading: { m: true } })}">
				${this.getTranslations().useLocationHeader}
			</h2>
		`;
	}

	private getBody(): RenderResult {
		return html`
			<p class="body ${Text.classnames({ body: { m: true } })}">
				${this.getTranslations().useLocationDescription}
			</p>
		`;
	}

	private getInput(): RenderResult {
		const isInvalid =
			this.state === ZipInCardStateEnum.emptyInput ||
			this.state === ZipInCardStateEnum.invalidInput;

		return html`
			<skapa-input-field auto-id="zip-in-card-input" state="${this.getInputState()}">
				<label slot="label">${this.getTranslations().zipCodeLabel}</label>
				<input
					.value="${this.zipCode ?? ''}"
					inputmode="text"
					@focusin="${this.onInputFocusIn.bind(this)}"
					@focusout="${this.onInputFocusOut.bind(this)}"
					@keydown="${this.onInputKeydown.bind(this)}"
					@input="${this.onInputValue.bind(this)}"
					aria-required="true"
				></input>
				<skapa-helper-text slot="helper">
					<span aria-hidden="${isInvalid}">${this.getHelperText()}</span>
					<span class="visuallyHidden" role="alert"
						>${isInvalid ? this.getHelperText() : null}</span
					>
				</skapa-helper-text>
			</skapa-input-field>
		`;
	}

	private getInputState(): InputFieldValidState {
		switch (this.state) {
			case ZipInCardStateEnum.invalidInput:
			case ZipInCardStateEnum.emptyInput:
				return 'error';
			default:
				return 'regular';
		}
	}

	private onInputFocusIn(): void {
		const inputElement = this.getInputElement();

		if (inputElement) {
			this.keyboardManager.setFocus(inputElement);
		}
	}

	private onInputFocusOut(): void {
		this.keyboardManager.removeFocus();
	}

	private onInputValue(e: InputEvent): void {
		e.stopPropagation();

		this.dispatchEvent(
			new CustomEvent('input', {
				bubbles: true,
				detail: {
					value: this.getZipCode()
				}
			})
		);
	}

	private onInputKeydown(event: KeyboardEvent): void {
		if (event.key !== 'Enter' || this.state === ZipInCardStateEnum.loading) {
			return;
		}

		this.dispatchEvent(
			new CustomEvent('save', {
				bubbles: true,
				detail: {
					zipCode: this.getZipCode()
				}
			})
		);
	}

	private getHelperText(): string {
		switch (this.state) {
			case ZipInCardStateEnum.invalidInput:
				return this.getTranslations().zipCodeInvalidError;
			case ZipInCardStateEnum.emptyInput:
				return this.getTranslations().zipCodeEmptyError;
			default:
				return this.getTranslations().zipCodeHelpText;
		}
	}

	private getInlineMessage(): RenderResult {
		return html`<div role="alert">${this.getErrorMessage()}</div>`;
	}

	private getErrorMessage(): RenderResult {
		if (this.state !== ZipInCardStateEnum.noZipInfo) {
			return null;
		}

		const cautionaryVariant: InlineMessageVariant = 'cautionary';
		const { ikeaErrorTitleShort, ikeaGenericErrorTryAgain } = this.getTranslations();

		return html`
			<skapa-inline-message variant="${cautionaryVariant}">
				<skapa-icon slot="message-icon" icon="warning-triangle"></skapa-icon>
				<span slot="title">${ikeaErrorTitleShort}</span>
				<span slot="body">${ikeaGenericErrorTryAgain}</span>
			</skapa-inline-message>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-input-field': SkapaInputField;
		'skapa-inline-message': SkapaInlineMessage;
		'skapa-icon': SkapaIcon;
		'skapa-helper-text': SkapaHelperText;
		'kompis-zip-in-card': ZipInCard;
	}
}
